import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pause, Play, Volume2, VolumeX, type LucideIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Slider } from '../../ui/slider';

/** A pin standing on the timeline at a moment of the clip, e.g. a celebration. */
export interface TimelinePin {
  id: string;
  /** Where it stands, as a fraction of the clip [0,1]. */
  at: number;
  icon: LucideIcon;
  label: string;
  selected?: boolean;
}

interface VideoTrimBarProps {
  video: HTMLVideoElement | null;
  trimStart: number;   // [0,1]
  trimEnd: number;     // [0,1]
  isPlaying: boolean;
  volume: number;      // [0,1]
  onPlayPause: () => void;
  onTrimStartChange: (val: number) => void;
  onTrimEndChange: (val: number) => void;
  onVolumeChange: (val: number) => void;
  /** Pins standing on the timeline: dragged along it, tapped to pick one. */
  pins?: TimelinePin[];
  onPinMove?: (id: string, at: number) => void;
  onPinTap?: (id: string) => void;
}

export function VideoTrimBar({ video, trimStart, trimEnd, isPlaying, volume, onPlayPause, onTrimStartChange, onTrimEndChange, onVolumeChange, pins = [], onPinMove, onPinTap }: VideoTrimBarProps) {
  const { t } = useTranslation();
  const [cursor, setCursor] = useState(0); // [0,1] current playback position
  const trackRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);

  // Sync cursor position with video.currentTime via rAF
  useEffect(() => {
    const tick = () => {
      if (video && video.duration > 0) {
        setCursor(video.currentTime / video.duration);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current !== null) cancelAnimationFrame(rafRef.current); };
  }, [video]);

  // Pointer drag helpers — returns [0,1] fraction from pointer position on track
  const toFraction = (e: React.PointerEvent<HTMLDivElement>): number => {
    const rect = e.currentTarget.getBoundingClientRect();
    return Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
  };

  // Each handle type: 'start' | 'end' | 'cursor'
  const draggingRef = useRef<'start' | 'end' | 'cursor' | null>(null);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>, type: 'start' | 'end' | 'cursor') => {
    draggingRef.current = type;
    e.currentTarget.setPointerCapture(e.pointerId);
    e.stopPropagation();
  };

  const handleTrackPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    const frac = toFraction(e);
    if (draggingRef.current === 'start') {
      onTrimStartChange(frac);
    } else if (draggingRef.current === 'end') {
      onTrimEndChange(frac);
    } else if (draggingRef.current === 'cursor' && video && video.duration > 0) {
      const clamped = Math.max(trimStart, Math.min(trimEnd, frac));
      video.currentTime = clamped * video.duration;
    }
  };

  const handleTrackPointerUp = () => { draggingRef.current = null; };

  // A pin: dragged along the track, or — moved no more than a wobble — tapped.
  const pinDragRef = useRef<{ id: string; x: number; moved: boolean } | null>(null);

  const handlePinPointerDown = (e: React.PointerEvent<HTMLButtonElement>, id: string) => {
    pinDragRef.current = { id, x: e.clientX, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    e.stopPropagation();
  };

  const handlePinPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const dragging = pinDragRef.current;
    const rect = trackRef.current?.getBoundingClientRect();
    if (!dragging || !rect || rect.width === 0) return;
    if (!dragging.moved && Math.abs(e.clientX - dragging.x) < 4) return;
    dragging.moved = true;
    onPinMove?.(dragging.id, Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)));
  };

  const handlePinPointerUp = () => {
    const dragging = pinDragRef.current;
    pinDragRef.current = null;
    if (dragging && !dragging.moved) onPinTap?.(dragging.id);
  };

  const fmt = (frac: number) => {
    if (!video || !video.duration) return '0:00';
    const s = Math.round(frac * video.duration);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };

  // Clamp cursor display within trimmed region
  const cursorDisplay = Math.max(trimStart, Math.min(trimEnd, cursor));

  const roundButton = 'flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-foreground transition-colors hover:bg-accent';

  return (
    // The timeline, with the sound and play/pause on its right, level with the track.
    <div className="flex items-end gap-2 select-none">
      {/* Timeline track */}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {/* Time labels */}
        <div className="flex justify-between text-[10px] text-muted-foreground px-0.5">
          <span>{fmt(trimStart)}</span>
          <span>{fmt(cursor)}</span>
          <span>{fmt(trimEnd)}</span>
        </div>

        {/* Pins, standing on the track where they are: the head above it, a
            stem down to the moment. Above the track rather than in it, so they
            never get in the way of the trim handles. */}
        {pins.length > 0 && (
          <div className="relative h-7" data-testid="timeline-pins">
            {pins.map((pin) => {
              const Icon = pin.icon;
              return (
                <div key={pin.id} className="absolute top-0 -translate-x-1/2" style={{ left: `${pin.at * 100}%` }}>
                  <button
                    type="button"
                    className={`relative z-30 flex size-6 cursor-grab touch-none items-center justify-center rounded-full shadow ${
                      pin.selected ? 'bg-primary text-primary-foreground ring-2 ring-primary/40' : 'bg-background text-foreground ring-1 ring-border'
                    }`}
                    onPointerDown={(e) => handlePinPointerDown(e, pin.id)}
                    onPointerMove={handlePinPointerMove}
                    onPointerUp={handlePinPointerUp}
                    onPointerCancel={() => { pinDragRef.current = null; }}
                    aria-label={pin.label}
                    aria-pressed={pin.selected ?? false}
                    data-testid="timeline-pin"
                  >
                    <Icon className="size-3" aria-hidden />
                  </button>
                  {/* The stem, down through the track to the moment. */}
                  <div
                    className={`pointer-events-none absolute left-1/2 top-6 z-30 h-7 w-0.5 -translate-x-1/2 ${pin.selected ? 'bg-primary' : 'bg-foreground/60'}`}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Track */}
        <div
          ref={trackRef}
          className="relative h-6 rounded cursor-pointer touch-none"
          onPointerMove={handleTrackPointerMove}
          onPointerUp={handleTrackPointerUp}
          onPointerLeave={handleTrackPointerUp}
        >
          {/* Full track background */}
          <div className="absolute inset-y-0 left-0 right-0 rounded bg-muted" />

          {/* Active (trimmed) segment */}
          <div
            className="absolute inset-y-0 bg-primary/30 rounded"
            style={{ left: `${trimStart * 100}%`, right: `${(1 - trimEnd) * 100}%` }}
          />

          {/* Start handle */}
          <div
            className="absolute top-0 bottom-0 w-3 -translate-x-1/2 flex items-center justify-center cursor-ew-resize z-20"
            style={{ left: `${trimStart * 100}%` }}
            onPointerDown={e => handlePointerDown(e, 'start')}
          >
            <div className="w-1.5 h-full rounded-full bg-primary" />
          </div>

          {/* End handle */}
          <div
            className="absolute top-0 bottom-0 w-3 -translate-x-1/2 flex items-center justify-center cursor-ew-resize z-20"
            style={{ left: `${trimEnd * 100}%` }}
            onPointerDown={e => handlePointerDown(e, 'end')}
          >
            <div className="w-1.5 h-full rounded-full bg-primary" />
          </div>

          {/* Playback cursor */}
          <div
            className="absolute top-0 bottom-0 w-3 -translate-x-1/2 flex items-center justify-center cursor-ew-resize z-10"
            style={{ left: `${cursorDisplay * 100}%` }}
            onPointerDown={e => handlePointerDown(e, 'cursor')}
          >
            <div className="w-0.5 h-full bg-white drop-shadow" />
          </div>
        </div>
      </div>

      {/* The sound: a tap opens its slider, upright, above the button. */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={roundButton}
            aria-label={t('shareClimb.volume')}
            data-testid="share-volume"
          >
            {volume === 0 ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
          </button>
        </PopoverTrigger>
        <PopoverContent side="top" align="center" className="flex h-36 w-11 justify-center px-0 py-3">
          <Slider
            orientation="vertical"
            min={0}
            max={1}
            step={0.05}
            value={[volume]}
            onValueChange={([value]) => onVolumeChange(value)}
            aria-label={t('shareClimb.volume')}
            data-testid="share-volume-slider"
          />
        </PopoverContent>
      </Popover>
      <button
        type="button"
        onClick={onPlayPause}
        className={roundButton}
        aria-label={t(isPlaying ? 'shareClimb.pause' : 'shareClimb.play')}
        data-testid="share-play"
      >
        {isPlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}